
<template>
  <div
    v-if="org && group"
    class="row"
  >
    <div class="col-lg-10">
      <h2
        id="customer-admin-groupsheader"
      >
        <router-link :to="{ name: 'customer-admin-groups' }">
          {{ org.name }}
        </router-link>
        | Group: {{ group.name || 'New' }}
      </h2>
      <form @submit.prevent="save">
        <div class="form-group">
          <label
            for="groupName"
            class="control-label"
          >
            Name
          </label>
          <input
            id="groupName"
            v-model.trim="_group.name"
            type="text"
            class="form-control"
            placeholder="group name"
            name="name"
            style="max-width: 300px"
            required
            maxlength="100"
          >
        </div>
        <div
          v-if="!hidePrivacy"
          class="row"
          :style="org.settings.Assignments ? 'display: none; margin-top: 30px;' : 'margin-top: 30px;'"
        >
          <div class="form-group col-md-3">
            <label
              for="managerPrivacyLevel"
              class="control-label"
            >
              Coach Privacy Level
            </label>
            <select
              id="managerPrivacyLevel"
              v-model="_group.managerPrivacyLevel"
              class="form-control"
              style="max-width: 200px"
              @change="privacyChanged('managerPrivacyLevel')"
            >
              <option :value="1">
                Low
              </option>
              <option :value="2">
                Medium
              </option>
              <option :value="3">
                High
              </option>
              <option
                v-if="!org.settings.Assignments"
                :value="0"
              >
                Sharing Disabled
              </option>
            </select>
          </div>
          <div class="form-group col-md-9">
            <h2>Explanation of Coach Privacy Level</h2>
            <table class="privacy-docs">
              <caption>
                The Coach Privacy Level controls how much access
                a coach has to view the recordings created by the
                speakers in this group. Refer to
                the table below to implement the desired behavior.
              </caption>
              <tr>
                <th scope="col">
                  Level
                </th>
                <th scope="col">
                  Coach Access to Speaker Recordings
                </th>
                <th scope="col">
                  Coach Access to Speaker Feedback &amp; Scores
                </th>
              </tr>
              <tr>
                <th scope="row">
                  Low
                </th>
                <td>Coach can see and hear all recordings</td>
                <td>Coach can see and hear all feedback and scores</td>
              </tr>
              <tr>
                <th scope="row">
                  Medium
                </th>
                <td>Coach can see all recordings but hear only submitted or shared recordings</td>
                <td>
                  Coach can see speaker feedback/scores only on
                  recordings submitted to the coach or shared with the
                  group. Coach cannot
                  see feedback/scores on unsubmitted and unshared recordings.
                </td>
              </tr>
              <tr>
                <th scope="row">
                  High
                </th>
                <td>Coach can see and hear only submitted or shared recordings</td>
                <td>
                  Coach can see speaker feedback/scores only on
                  recordings submitted to the coach or shared with the
                  group. Coach cannot
                  see feedback/scores on unsubmitted and unshared recordings.
                </td>
              </tr>
              <tr v-if="!org.settings.Assignments">
                <th scope="row">
                  Sharing Disabled
                </th>
                <td>Coach can see all recordings, but hear none</td>
                <td>Feedback and Scores Disabled / None</td>
              </tr>
            </table>
          </div>
        </div>
        <div
          v-if="!hidePrivacy"
          class="row"
          style="margin-top: 30px;"
        >
          <div class="form-group col-md-3">
            <label
              for="speakerPrivacyLevel"
              class="control-label"
            >
              Speaker Privacy Level
            </label>
            <select
              id="speakerPrivacyLevel"
              v-model="_group.speakerPrivacyLevel"
              class="form-control"
              style="max-width: 200px"
              @change="privacyChanged('speakerPrivacyLevel')"
            >
              <option :value="1">
                Low
              </option>
              <option :value="2">
                Medium
              </option>
              <option :value="3">
                High
              </option>
              <option :value="4">
                Highest
              </option>
              <option
                v-if="!org.settings.Assignments"
                :value="0"
              >
                Sharing Disabled
              </option>
            </select>
          </div>
          <div class="form-group col-md-9">
            <h2>Explanation of Speaker Privacy Level</h2>
            <table class="privacy-docs">
              <caption>
                The Speaker Privacy Level controls how much access
                speakers have to view the recordings created by the other
                speakers in this group. Refer to
                the table below to implement the desired behavior.
              </caption>
              <tr>
                <th scope="col">
                  Level
                </th>
                <th scope="col">
                  Speaker Access to Peer Recordings
                </th>
                <th scope="col">
                  Speaker Access to Feedback &amp; Scores
                </th>
              </tr>
              <tr>
                <th scope="row">
                  Low
                </th>
                <td>
                  Speakers can see and hear only recordings that were submitted for peer-review.
                  Speakers can see a list of all the other speakers.
                </td>
                <td>Speakers can see all feedback and scores on recordings that were submitted for peer-review</td>
              </tr>
              <tr>
                <th scope="row">
                  Medium
                </th>
                <td>
                  Speakers can see and hear only recordings that were submitted for peer-review.
                  Speakers can see a list of all the other speakers.
                </td>
                <td>
                  Speakers can see all speaker feedback and scores on
                  recordings that were submitted for peer-review, but cannot see
                  coach feedback or scores, except on
                  their own recordings
                </td>
              </tr>
              <tr>
                <th scope="row">
                  High
                </th>
                <td>
                  Speakers can see and hear only recordings that were submitted for peer-review.
                  Speakers can see a list of only the speakers who shared at least one recording.
                </td>
                <td>
                  Speakers cannot see speaker or
                  coach feedback and scores, except on
                  their own recordings
                </td>
              </tr>
              <tr>
                <th scope="row">
                  Highest
                </th>
                <td>
                  None. Ability to share recordings with fellow speakers disabled.
                  Prevents a list of all speakers from being shown.
                </td>
                <td>
                  Speaker feedback disabled. Speakers can only see
                  coach feedback and scores on their own recordings
                </td>
              </tr>
              <tr v-if="!org.settings.Assignments">
                <th scope="row">
                  Sharing Disabled
                </th>
                <td>
                  None. Ability to share recordings with fellow speakers disabled.
                  Prevents a list of all speakers from being shown.
                </td>
                <td>Feedback and Scores Disabled / None</td>
              </tr>
            </table>
          </div>
        </div>
        <loading-button :loading="saving">
          Save
        </loading-button>
      </form>
    </div>
  </div>
</template>

<style lang="less" scoped>
  table.privacy-docs {
    width: 100%;
    border-collapse: collapse;

    td, th {
      padding: 5px;
      width: 38%;
      border: 1px solid #28275D;
    }

    th {
      font-size: 14px;
    }

    td:first-child, th:first-child {
      width: 24%;
    }

    tr:nth-child(odd) {background-color: #f2f2f2;}
  }
</style>

<script>
export default {
  props: {
    hidePrivacy: {
      type: Boolean,
      default: false
    },
    org: {
      type: Object,
      required: true
    },
    group: {
      //type: Object,
      //required: true
    },
    isEdu: {
      type: Boolean,
      default: false
    },
    groups: {
      type: Array,
      required: true
    },
    groupIdsToNames: {},
  },
  data() {
    return {
      saving: false,
    };
  },
  computed: {
    _group() {
      return this._.cloneDeep(this.group); // clone group to avoid mutation warnings
    }
  },
  methods: {
    privacyChanged(current) {
      const other = current === 'speakerPrivacyLevel' ? 'managerPrivacyLevel' : 'speakerPrivacyLevel';

      if (this._group[current] === 0 && this._group[other] !== 0) {
        this._group[other] = 0;
      } else if (this._group[current] !== 0 && this._group[other] === 0) {
        this._group[other] = (current === 'speakerPrivacyLevel') ? 2 : 4;
      }
      this.$forceUpdate();
    },
    save() {
      this.saving = true;
      this.errors = [];
      let fields = (this.hidePrivacy) ?
        ['groupId', 'name'] :
        ['groupId', 'speakerPrivacyLevel', 'managerPrivacyLevel', 'name'];
      for (let key in this._group) {
        if (fields.indexOf(key) === -1) {
          delete this._group[key];
        }
      }

      if (this.group.groupId === 0) {
        delete this._group.groupId;
        this._group.orgName = this.$store.getters.org.name;
      }

      const action = (this.group.groupId === 0) ? 'addGroup' : 'updateGroup';
      return this.$store.dispatch(action, this._group)
        .then(() => {
          this.$router.push({name: 'customer-admin-groups'});
        })
        .catch(() => {
          alert('The group could not be saved. Most likely, the name you entered is already used elsewhere. ' +
              'Please change the name to be unique.');
        })
        .finally(() => {
          this.saving = false;
        });
    },
  }
};
</script>
